import type { FilterDefinition } from '@/constants/filters'
import { defineStore, acceptHMRUpdate } from 'pinia'
import { ref } from 'vue'
import { useCollectionStore } from './collection'
import storeConfigs from 'constants/storeConfigs.ts'

export type Filter = {
	collectionName: string
	value: any
	definition: FilterDefinition
}

export const useCollectionFilterStore = defineStore(
	storeConfigs.collectionFilter.name,
	() => {
		const { setReload } = useCollectionStore()

		const items = ref<Filter[]>([])

		function get(collectionName: string) {
			return items.value.filter(
				(item) => item.collectionName === collectionName
			)
		}

		function addOrUpdateItem(data?: Filter) {
			if (!data) return
			const index = items.value.findIndex(
				(item) =>
					item.collectionName === data.collectionName &&
					item.definition.component === data.definition.component
			)

			setReload(data.collectionName, true)

			if (!data.value) {
				removeItem(data)
				return
			}

			if (index !== -1) {
				items.value[index] = data
			} else {
				items.value.push(data)
			}
		}

		function removeItem(data: Pick<Filter, 'collectionName' | 'definition'>) {
			const index = items.value.findIndex(
				(item) =>
					item.collectionName === data.collectionName &&
					item.definition.component === data.definition.component
			)
			if (index !== -1) {
				items.value.splice(index, 1)
				setReload(data.collectionName, true)
			}
		}

		function removeItems(collectionName: string) {
			items.value.filter((item) => item.collectionName !== collectionName)
			setReload(collectionName, true)
		}

		function getFormatted(collectionName: string) {
			const data: {
				q?: string[]
				v?: string[]
				tsgt?: number
				tslt?: number
			} = {}

			get(collectionName).forEach((filter) => {
				let value
				const { definition } = filter

				if (filter.definition.type === 'date' && filter.value.ts !== '') {
					const startDate = new Date(filter.value.ts * 1000)
					const endDate = new Date(filter.value.ts * 1000)
					startDate.setHours(0)
					startDate.setMinutes(0)
					startDate.setSeconds(0)
					endDate.setHours(23)
					endDate.setMinutes(59)
					endDate.setSeconds(59)
					data.tsgt = startDate.getTime() / 1000
					data.tslt = endDate.getTime() / 1000
				} else if (typeof filter.value === 'object') {
					value = filter.value[definition.value.field]
				} else {
					value = filter.value
				}
				if (value && definition) {
					data.q = data.q || []
					data.v = data.v || []
					data.q.push(definition.field)
					data.v.push(value)
				}
			})

			return data
		}

		function $reset() {
			items.value = []
		}

		return {
			items,
			get,
			getFormatted,
			addOrUpdateItem,
			removeItem,
			removeItems,
			$reset,
		}
	},
	{
		persist: storeConfigs.collectionFilter.persistAll,
	}
)

if (import.meta.hot) {
	import.meta.hot.accept(
		acceptHMRUpdate(useCollectionFilterStore, import.meta.hot)
	)
}
